import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Briefcase } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const positionOptions = [
  "Aviation Detailer",
  "Commercial / Office Cleaner",
  "Industrial / Warehouse Crew",
  "Event Crew",
  "Crew Lead / Supervisor",
  "Open to any role",
];
const experienceOptions = [
  "No experience (open to training)",
  "Less than 1 year",
  "1–3 years",
  "3–5 years",
  "5+ years",
];
const availabilityOptions = [
  "Full-time",
  "Part-time",
  "Weekends only",
  "Nights / Overnight",
  "Flexible",
];
const yesNo = ["Yes", "No"];

export default function ApplyDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    position: "",
    experience: "",
    availability: "",
    transportation: "",
    authorized: "",
    about: "",
  });

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () =>
    setForm({
      name: "",
      email: "",
      phone: "",
      location: "",
      position: "",
      experience: "",
      availability: "",
      transportation: "",
      authorized: "",
      about: "",
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone");
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/apply`, form);
      if (res.data?.success) {
        toast.success(
          "Application received! Tidy will reach out within 3 business days."
        );
        reset();
        setOpen(false);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        "Couldn't submit right now. Please email trytidycleanup@gmail.com."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#f4ebde] border-[#143527]/15 text-[#143527] p-0">
        <div className="bg-[#143527] text-[#f4ebde] px-7 lg:px-9 pt-7 pb-6 rounded-t-lg">
          <DialogHeader className="space-y-3 text-left">
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-[#e8a428]" strokeWidth={1.8} />
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#e8a428]">
                Join the Crew
              </span>
            </div>
            <DialogTitle className="font-serif text-3xl lg:text-4xl text-[#f4ebde] leading-tight">
              Apply to join Tidy
            </DialogTitle>
            <DialogDescription className="text-[#f4ebde]/75 text-sm leading-relaxed">
              Tell us a little about yourself. We&rsquo;ll review your application and reach out within
              3 business days. No experience? Paid training available.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="px-7 lg:px-9 py-7 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Full name *</Label>
              <Input
                value={form.name}
                onChange={onInput("name")}
                placeholder="Jane Smith"
                className="bg-white border-[#143527]/20 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Phone *</Label>
              <Input
                value={form.phone}
                onChange={onInput("phone")}
                placeholder="(910) 555-0100"
                className="bg-white border-[#143527]/20 h-11 rounded-xl"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={onInput("email")}
                placeholder="you@example.com"
                className="bg-white border-[#143527]/20 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">City / Town</Label>
              <Input
                value={form.location}
                onChange={onInput("location")}
                placeholder="Southern Pines, NC"
                className="bg-white border-[#143527]/20 h-11 rounded-xl"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Position interested in</Label>
              <Select value={form.position} onValueChange={setField("position")}>
                <SelectTrigger className="bg-white border-[#143527]/20 h-11 rounded-xl">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent className="bg-[#f4ebde]">
                  {positionOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Cleaning experience</Label>
              <Select value={form.experience} onValueChange={setField("experience")}>
                <SelectTrigger className="bg-white border-[#143527]/20 h-11 rounded-xl">
                  <SelectValue placeholder="Select experience" />
                </SelectTrigger>
                <SelectContent className="bg-[#f4ebde]">
                  {experienceOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Availability</Label>
              <Select value={form.availability} onValueChange={setField("availability")}>
                <SelectTrigger className="bg-white border-[#143527]/20 h-11 rounded-xl">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#f4ebde]">
                  {availabilityOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Reliable transportation?</Label>
              <Select value={form.transportation} onValueChange={setField("transportation")}>
                <SelectTrigger className="bg-white border-[#143527]/20 h-11 rounded-xl">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#f4ebde]">
                  {yesNo.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">US work authorized?</Label>
              <Select value={form.authorized} onValueChange={setField("authorized")}>
                <SelectTrigger className="bg-white border-[#143527]/20 h-11 rounded-xl">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#f4ebde]">
                  {yesNo.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">Tell us about yourself</Label>
            <textarea
              value={form.about}
              onChange={onInput("about")}
              rows={4}
              placeholder="Past experience, certifications, why you'd be a great fit..."
              className="w-full bg-white border border-[#143527]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#143527]/30"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="rounded-full w-full py-6 btn-primary text-[15px] group disabled:opacity-70"
          >
            {submitting ? "Sending..." : "Submit application"}
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="text-xs text-[#143527]/60 text-center">
            By submitting, you agree to be contacted about your application.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
